import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import { Container, Loader } from "semantic-ui-react";

import { MHPTreatmentReport as MHPTreatmentReportCommon } from "@teamprevent/common/mentalHealthPractitionerTreatment/components/MHPTreatmentReport";
import { ViewClinicalHistoryButton } from "@teamprevent/common/referralReport/components/ViewClinicalHistoryButton";
import { AnsweredQuestionWithId, ConsultationDetails } from "@teamprevent/common/model";
import { getMHPTreatmentReportModel, MHPTreatmentReportModel, MHP_TREATMENT_REPORT_KEY } from "@teamprevent/common/mentalHealthPractitionerTreatment/models";
import { clearQuestionnaire } from "@teamprevent/common/questionnaires/actions";
import { navigateToParentRoute } from "@teamprevent/common/routing/actions";
import { toast } from "@teamprevent/common/toasts";

import { ApplicationState } from "../../applicationState";
import { AddNextTaskFormModel } from "../../tasks/models";
import { AddTasksInputs } from "../../tasks/components/addTasksInputs";
import { TaskType, mapToCreateTaskModel } from "../../task/model";
import { mhpTreatmentApi } from "../mhpTreatmentApi";
import {
    loadMHPTreatmentReport,
    loadMHPTreatmentReportPreview,
    saveMHPTreatmentReport,
    submitMHPTreatmentReport,
    submitMHPTreatmentReportAmendment } from "../actions";

import { PreviewMHPTreatmentReportButton } from "./PreviewMHPTreatmentReportButton";
import { ViewMHPTreatmentConsultationButton } from "./ViewMHPTreatmentConsultationButton";

interface Props {
    isReportPreview: boolean;
    nextTaskRequired: boolean;
    isReportAmendment: boolean;
}

export const MHPTreatmentReport: React.FunctionComponent<Props> = (props: Props) => {

    const dispatch = useDispatch();

    const customerId = useSelector((state: ApplicationState) => state.router.params?.customerId);
    const employeeRecordId = useSelector((state: ApplicationState) => state.router.params?.employeeRecordId);
    const ohCaseId = useSelector((state: ApplicationState) => state.router.params?.ohCaseId);
    const taskId = useSelector((state: ApplicationState) => state.router.params?.taskId);

    const employeeRecord = useSelector((state: ApplicationState) => state.employeeRecordsDetail?.employeeRecords?.find(er => er.id === employeeRecordId));
    const ohCase = useSelector((state: ApplicationState) => state.ohCases?.find(c => c.id === ohCaseId));
    const questionnaire = useSelector((state: ApplicationState) => state.questionnaires[MHP_TREATMENT_REPORT_KEY]);

    const task = ohCase?.tasks.find(t => t.id === taskId);

    const readonly = props.isReportPreview || task?.completed;

    const [nextTaskTypes, setNextTaskTypes] = React.useState<TaskType[]>(null);
    const [consultationDetails, setConsultationDetails] = React.useState<ConsultationDetails>(null);

    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [nextTasks, setNextTasks] = React.useState<AddNextTaskFormModel[]>(null);
    const [report, setReport] = React.useState<MHPTreatmentReportModel>(null);

    React.useEffect(() => {
        setReport(getMHPTreatmentReportModel(questionnaire));
    }, [questionnaire]);

    React.useEffect(() => {
        if (props.nextTaskRequired) {
            setNextTasks([{ task: { taskType: null, taskCode: null, dueDate: null, notes: null }, isValid: false }]);
        }
    }, [props.nextTaskRequired]);

    const extraSections = !readonly && nextTaskTypes && props.nextTaskRequired ? [{
        id: "NextTask",
        title: "Next Task",
        content: (
            <AddTasksInputs
                taskTypes={nextTaskTypes}
                onChange={setNextTasks}
                ohCaseId={ohCaseId}
                showErrors={submitted}
                assignmentOptionRequired={true}
            />)
    }] : [];

    React.useEffect(() => {
        (async () => {
            if (ohCaseId && taskId) {
                const [taskTypes, details] = await Promise.all([
                    mhpTreatmentApi.getConsultationNextTaskTypes(),
                    mhpTreatmentApi.getMHPTreatmentConsultationDetailsForReport(ohCaseId, taskId)
                ]);

                setNextTaskTypes(taskTypes);
                setConsultationDetails(details);
            }
        })();

        return () => {
            dispatch(clearQuestionnaire(MHP_TREATMENT_REPORT_KEY));
        };
    }, [dispatch, ohCaseId, taskId, readonly]);

    const loadReport = React.useCallback(async () => {
        if (props.isReportPreview) {
            dispatch(loadMHPTreatmentReportPreview(ohCaseId, taskId));
        } else {
            dispatch(loadMHPTreatmentReport(ohCaseId, taskId));
        }
    }, [dispatch, ohCaseId, taskId, props.isReportPreview]);

    const saveReport = React.useCallback(async (updatedQuestions: AnsweredQuestionWithId[], eTag: string) => {
        dispatch(saveMHPTreatmentReport(ohCaseId, taskId, updatedQuestions, eTag));
    }, [dispatch, ohCaseId, taskId]);

    const submitReport = React.useCallback(async (): Promise<boolean> => {
        setSubmitted(true);

        if (report?.questions?.some(q => !q.isValid)) {
            toast.warning("Some questions are invalid");
            return false;
        }

        const noNextTasks = !nextTasks || nextTasks?.length === 0;
        if (props.nextTaskRequired && (noNextTasks || nextTasks?.some(t => !t.isValid))) {
            toast.warning("No valid \"Next Tasks\" have been set");
            return false;
        }

        const createTasks = nextTasks?.map(t => mapToCreateTaskModel(t.task, ohCaseId));

        if (props.isReportAmendment)
        {
            dispatch(submitMHPTreatmentReportAmendment(ohCaseId, taskId, createTasks));
        }
        else
        {
            dispatch(submitMHPTreatmentReport(ohCaseId, taskId, createTasks));
        }

        dispatch(navigateToParentRoute());

        return true;
    }, [dispatch, nextTasks, ohCaseId, props.isReportAmendment, props.nextTaskRequired, report?.questions, taskId]);

    const adminButtons = (
        <>
            <PreviewMHPTreatmentReportButton
                customerId={customerId}
                employeeRecordId={employeeRecordId}
                ohCaseId={ohCaseId}
                taskId={taskId}
            />
            <ViewClinicalHistoryButton
                customerId={customerId}
                employeeRecordId={employeeRecordId}
            />
            <ViewMHPTreatmentConsultationButton
                customerId={customerId}
                employeeRecordId={employeeRecordId}
                ohCaseId={ohCaseId}
                consultationTaskId={task?.precedingTaskId}
            />
        </>
    );

    if (!ohCase || !task || !report || (!readonly && (!nextTaskTypes || !consultationDetails))) {
        return (
            <Container className="referral-report">
                <Loader active />
            </Container>
        );
    }

    return (
        <MHPTreatmentReportCommon
            readonly={props.isReportPreview || task?.completed}
            customerId={customerId}
            employeeRecord={employeeRecord}
            ohCase={ohCase}
            task={task}
            report={report}
            isFromAdmin={true}
            isReportPreview={props.isReportPreview}
            adminButtons={adminButtons}
            loadReport={loadReport}
            saveReport={saveReport}
            submitReport={submitReport}
            consultationAndReferralDetails={consultationDetails}
            extraSections={extraSections}
        />
    );
};
